import React, { useState, useEffect } from 'react'
import { heatColor, RESPONSE_SCALE, getGroupTheme, runColumnLabel } from '../../../constants/PulseCheckAnalyticsConstants'
import { useTranslation } from 'react-i18next'

const OVERALL = 'overall'

// Pick the displayed value for a cell given the selected theme (overall or a
// specific group). Returns null when the entity has no response for that run.
const cellValue = (cell, theme) => {
    if (!cell) return null
    const value = theme === OVERALL ? cell.overall : cell.groups?.[theme]
    return value === null || value === undefined ? null : value
}

const DIMENSIONS = [
    { key: 'student', label: 'Student' },
    { key: 'class', label: 'Class' },
    { key: 'year', label: 'Year' },
]

const rowHasResults = (row) => (row.cells || []).some((c) => c && c.overall !== null && c.overall !== undefined)

function HeatMapCard({ heatmap, groups = [], density = 'comfy', view = 'class', onViewChange }) {
    const { t } = useTranslation()
    const [dimension, setDimension] = useState(view === 'year' ? 'year' : 'class')
    const [theme, setTheme] = useState(OVERALL)
    const [showEmpty, setShowEmpty] = useState(true)

    const cellH = density === 'dense' ? 28 : 34
    const cellGap = density === 'dense' ? 4 : 6

    const columns = heatmap?.columns || []
    const studentAvailable = !!heatmap?.student_available
    const activeDimension = dimension === 'student' && !studentAvailable ? 'class' : dimension

    // Surface the effective view to the parent so a downloaded report matches
    // whatever the user is currently looking at (Student / Class / Year).
    useEffect(() => {
        onViewChange?.(activeDimension)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDimension])

    const allRows = heatmap?.[activeDimension] || []
    const emptyCount = allRows.filter((r) => !rowHasResults(r)).length
    const rows = showEmpty ? allRows : allRows.filter(rowHasResults)

    const themeOptions = [{ key: OVERALL, label: 'Overall', color: '#5E639B' }, ...groups.map((g, i) => ({ key: g, label: g, color: getGroupTheme(g, i).color }))]
    const themeLabel = themeOptions.find((o) => o.key === theme)?.label ?? 'Overall'

    const titleByDimension = { student: 'Student Overview', class: 'Class Overview', year: 'Year Overview' }
    const subtitleByDimension = {
        student: 'Heatmap of student responses',
        class: 'Heatmap of class averages',
        year: 'Heatmap of year-group averages',
    }

    return (
        <div className="flex-1 min-w-0 bg-white rounded-2xl px-[24px] py-[22px] border border-lightBG shadow-[0_12px_30px_rgba(45,43,69,0.06),0_1px_0_rgba(45,43,69,0.02)]">
            {/* Header: title + view selector */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="font-bold text-[18px] text-darkenPurple">{t(titleByDimension[activeDimension])}</div>
                    <div className="font-medium text-[12px] text-mudPurple">
                        {t(subtitleByDimension[activeDimension])} · {t(themeLabel)} · {t('Avg 1–5 per pulse run')}
                    </div>
                </div>
                <ViewSelector dimension={activeDimension} setDimension={setDimension} studentAvailable={studentAvailable} />
            </div>

            {/* Group selector */}
            <div className="mt-[14px]">
                <ThemeFilter theme={theme} setTheme={setTheme} options={themeOptions} />
            </div>

            {/* Legend + show/hide non-respondents */}
            <div className="flex items-center justify-between gap-4 flex-wrap mt-[14px] pb-[14px] border-b border-lightBG">
                <div className="flex gap-[8px] flex-wrap">
                    {RESPONSE_SCALE.slice()
                        .reverse()
                        .map((s) => (
                            <span
                                key={s.v}
                                className="inline-flex items-center gap-2 px-[12px] py-[5px] bg-white border border-lightBG rounded-full shadow-[0_2px_6px_rgba(45,43,69,0.04)]"
                            >
                                <span className="w-[10px] h-[10px] rounded-full" style={{ background: s.color }} />
                                <span className="font-semibold text-[12px] text-darkenPurple">{t(s.label)}</span>
                            </span>
                        ))}
                </div>
                {/* Only offer the toggle when there are non-respondent rows to reveal/hide. */}
                {emptyCount > 0 && (
                    <Toggle on={showEmpty} setOn={setShowEmpty} label={`${t('Show non-respondents')} (${emptyCount})`} />
                )}
            </div>

            {/* Grid */}
            {columns.length === 0 || rows.length === 0 ? (
                <div className="py-10 text-center text-mudPurple font-medium text-[14px]">{t('thereIsNoDataToShow')}</div>
            ) : (
                <div
                    className="grid items-center overflow-x-auto mt-[16px]"
                    style={{ gridTemplateColumns: `140px repeat(${columns.length},minmax(54px,1fr))`, columnGap: cellGap, rowGap: cellGap }}
                >
                    <div />
                    {columns.map((col, i) => (
                        <div key={i} className="text-center font-semibold text-[11px] text-mudPurple leading-tight">
                            {runColumnLabel(col, i, t('Run'))}
                        </div>
                    ))}

                    {rows.map((row) => {
                        // Secondary label under the row name: a class shows its year group,
                        // a student shows the class(es) they belong to.
                        const subLabel = activeDimension === 'class' ? row.year : activeDimension === 'student' ? row.class : ''
                        return (
                        // Key on the entity id when present (student rows carry a unique id);
                        // class/year rows have unique names. Avoids duplicate-key collisions
                        // when distinct students share a display label (e.g. several "Erika O."),
                        // which otherwise breaks reconciliation when switching the view dimension.
                        <React.Fragment key={`${activeDimension}-${row.id ?? row.name}`}>
                            <div className="pr-[10px] min-w-0">
                                <div className="font-semibold text-[13px] text-darkenPurple truncate">{row.name}</div>
                                {subLabel ? <div className="font-medium text-[11px] text-mudPurple truncate leading-tight">{subLabel}</div> : null}
                            </div>
                            {columns.map((col, i) => {
                                const raw = cellValue(row.cells?.[i], theme)
                                if (raw === null) {
                                    return <div key={i} className="rounded-[6px] grid place-items-center bg-[#F4F3FA] text-[#BEBECE] text-[12px]" style={{ height: cellH }} />
                                }
                                const c = heatColor(raw)
                                return (
                                    <div
                                        key={i}
                                        className="rounded-[6px] font-bold text-[13px] grid place-items-center"
                                        style={{ height: cellH, background: c.bg, color: c.fg }}
                                        title={`${raw}`}
                                    >
                                        {raw}
                                    </div>
                                )
                            })}
                        </React.Fragment>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default HeatMapCard

const Toggle = ({ on, setOn, label }) => (
    <button onClick={() => setOn(!on)} className="inline-flex items-center gap-2 cursor-pointer">
        <span className={`relative w-[34px] h-[20px] rounded-full transition-colors ${on ? 'bg-deepPurple' : 'bg-[#D4D3E4]'}`}>
            <span className={`absolute top-[2px] w-[16px] h-[16px] bg-white rounded-full transition-all ${on ? 'left-[16px]' : 'left-[2px]'}`} />
        </span>
        <span className="text-[12px] font-semibold text-mudPurple">{label}</span>
    </button>
)

const ViewSelector = ({ dimension, setDimension, studentAvailable }) => {
    const { t } = useTranslation()
    return (
        <div className="inline-flex items-center gap-2">
            <span className="text-[12px] font-medium text-mudPurple">{t('View as')}:</span>
            <div className="inline-flex items-center p-[3px] bg-[#F4F3FA] border border-lightBG rounded-full">
                {DIMENSIONS.map((d) => {
                    const disabled = d.key === 'student' && !studentAvailable
                    const active = dimension === d.key
                    return (
                        <button
                            key={d.key}
                            disabled={disabled}
                            onClick={() => setDimension(d.key)}
                            title={disabled ? t('Apply a target filter to see individual students') : ''}
                            className={`px-[14px] py-[6px] rounded-full font-bold text-[12px] transition-all ${
                                active ? 'bg-deepPurple text-white' : disabled ? 'text-[#BEBECE] cursor-not-allowed' : 'text-darkenPurple'
                            }`}
                        >
                            {t(d.label)}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

const ThemeFilter = ({ theme, setTheme, options }) => {
    const { t } = useTranslation()
    return (
        <div className="inline-flex items-center gap-[6px] p-[4px] bg-[#F4F3FA] border border-lightBG rounded-full flex-wrap">
            {options.map((opt) => (
                <button
                    key={opt.key}
                    onClick={() => setTheme(opt.key)}
                    className={`inline-flex items-center gap-[7px] px-[12px] py-[6px] rounded-full font-bold text-[12px] cursor-pointer ${
                        theme === opt.key ? 'bg-deepPurple text-white' : 'bg-transparent text-darkenPurple'
                    }`}
                >
                    <span className="w-[8px] h-[8px] rounded-full" style={{ background: theme === opt.key ? '#fff' : opt.color }} />
                    {t(opt.label)}
                </button>
            ))}
        </div>
    )
}
